import { useCallback, useEffect, useRef, useState } from "react"
import {
  ActivityIndicator,
  Keyboard,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  TextInput,
  TextStyle,
  TouchableOpacity,
  TouchableWithoutFeedback,
  View,
  ViewStyle,
} from "react-native"

import { BottomSheetModal, BottomSheetModalProvider } from "@gorhom/bottom-sheet"
import { Image, ImageStyle } from "expo-image"
import { router } from "expo-router"
import { usePostHog } from "posthog-react-native"
import { useSafeAreaInsets } from "react-native-safe-area-context"

import { Alert } from "src/components/Alert"
import { BottomSheetListModal } from "src/components/BottomSheetListModal"
import { BottomSheetModalProvider as WebBottomSheetModalProvider } from "src/components/BottomSheetModalProvider"
import { BrandBanner } from "src/components/BrandBanner"
import { CloseButton } from "src/components/CloseButton"
import { Button, PasswordTextField, Text, TextField } from "src/components/Ignite"
import Config, { getEnvExtraVar } from "src/config"
import { useAuth } from "src/context/AuthContext"
import { useAvatarMode } from "src/context/AvatarModeContext"
import { useChannelLink } from "src/context/ChannelLinkContext"
import { useAppTheme } from "src/hooks/useAppTheme"
import { useUnauthChannelInfoQuery } from "src/queries/useUnauthChannelInfo"
import { ThemedStyle } from "src/theme"
import { getAppVersion, getNativeBuildNumber, getWebGitSha } from "src/utils/appVersion"
import { logSentryBreadcrumb } from "src/utils/crashReporting"
import { openLinkInBrowser } from "src/utils/openLinkInBrowser"
import { semverString } from "src/utils/stringUtils"
import { getEmailValidationError, isInternalEmail } from "src/utils/validateEmail"

// Web-specific interface for BottomSheetModal methods
interface BottomSheetModalMethods {
  present: () => void
  dismiss: () => void
  close: () => void
  snapToIndex: (index: number) => void
  snapToPosition: (position: string | number) => void
  expand: () => void
  collapse: () => void
  forceClose: () => void
}

export default function Login() {
  const { themed, theme } = useAppTheme()
  const posthog = usePostHog()
  const insets = useSafeAreaInsets()

  const [isWebKeyboardVisible, setIsWebKeyboardVisible] = useState(false)
  // Tracks visual viewport dimensions so we can pin the outer container to
  // exactly the visible area when the keyboard is open.
  //
  // On iOS Safari the layout viewport (window.innerHeight) shrinks when the
  // keyboard appears, but it still includes the URL-bar height (vv.offsetTop).
  // On Android Chrome the layout viewport may or may not shrink depending on
  // the browser's interactive-widget policy. In both cases vv.height is the
  // reliable "area above keyboard" value, and vv.offsetTop tells us where that
  // area starts inside the layout viewport.
  //
  // By clamping the KeyboardAvoidingView to { height: vv.height, marginTop:
  // vv.offsetTop } when the keyboard is visible we ensure the form fills
  // exactly the visible area — no gap below the content, no cropped buttons.
  // `webViewportBottom` is the Y coordinate (in layout-viewport space) of the
  // top of the keyboard — i.e. the bottom of the visible area above it.
  // = vv.offsetTop + vv.height
  //
  // We track this single value (not height and offsetTop separately) because:
  // • It is the only value needed for the maxHeight constraint.
  // • vv.offsetTop is volatile during keyboard animation; tracking the sum
  //   avoids stale-state artefacts from individual components racing.
  const [webViewport, setWebViewport] = useState<{ height: number; bottom: number }>(() => {
    if (Platform.OS === "web" && typeof window !== "undefined" && window.visualViewport) {
      const vv = window.visualViewport
      return { height: vv.height, bottom: vv.offsetTop + vv.height }
    }
    return { height: 0, bottom: 0 }
  })
  useEffect(() => {
    if (Platform.OS !== "web") return
    const viewport = window.visualViewport
    if (!viewport) return
    // Capture the initial full-screen height once so we can compare against it
    // regardless of browser. On iOS Safari, window.innerHeight stays fixed while
    // visualViewport.height shrinks. On Android Chrome both shrink together, so
    // we compare against the original innerHeight instead.
    const initialHeight = window.innerHeight
    const handleUpdate = () => {
      const h = viewport.height
      setWebViewport({ height: h, bottom: viewport.offsetTop + h })
      setIsWebKeyboardVisible(h < initialHeight * 0.75)
    }
    // "resize" fires when the keyboard opens/closes; "scroll" fires when the
    // browser shifts the visual viewport (e.g. URL-bar collapse on iOS Safari).
    viewport.addEventListener("resize", handleUpdate)
    viewport.addEventListener("scroll", handleUpdate)
    return () => {
      viewport.removeEventListener("resize", handleUpdate)
      viewport.removeEventListener("scroll", handleUpdate)
    }
  }, [])

  const hostPickerSheetRef = useRef<BottomSheetModal | BottomSheetModalMethods>(null)
  const { authHost } = useAuth()
  const { pendingChannelId } = useChannelLink()
  const isChannelDeeplink = !!pendingChannelId
  const { data: channelInfo } = useUnauthChannelInfoQuery(pendingChannelId)

  // Preserve the last non-undefined channel info so the branded banner stays visible
  // while the query cache is cleared during sign-in (clearAllTemporaryData → clearQueryCache).
  const [stableChannelInfo, setStableChannelInfo] = useState(channelInfo)
  useEffect(() => {
    if (channelInfo !== undefined) {
      setStableChannelInfo(channelInfo)
    }
  }, [channelInfo])

  const channelImageSource = stableChannelInfo?.brand_image
    ? { uri: stableChannelInfo.brand_image }
    : undefined
  const channelTitle = stableChannelInfo?.brand_title ?? stableChannelInfo?.name ?? undefined
  const channelSubtitle = stableChannelInfo?.brand_subtitle ?? undefined
  const channelSubscriptionUrl = stableChannelInfo?.subscription_url ?? null

  // Show the back-to-library close button only when the user arrived from a media-mode
  // content library. Absence of is_media_mode is treated as true per the API spec.
  const showBackToLibrary =
    isChannelDeeplink && !!pendingChannelId && stableChannelInfo?.is_media_mode !== false

  const localUserEmail = getEnvExtraVar("LOCAL_USER_EMAIL")
  const localUserPassword = getEnvExtraVar("LOCAL_USER_PASSWORD")
  const localAuthHost = getEnvExtraVar("LOCAL_AUTH_HOST")

  // Get the first segment of the host (e.g., "intrl" from "intrl.server.sidekickplatform.com")
  const hostFirstSegment = authHost.split(".")[0]
  const isDefaultHost = authHost === localAuthHost || authHost === Config.DEFAULT_AUTH_HOST

  const authEmailInput = useRef<TextInput>(null)
  const authPasswordInput = useRef<TextInput>(null)

  const [authEmail, setAuthEmail] = useState(localUserEmail || "")
  const [authPassword, setAuthPassword] = useState(localUserPassword || "")
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const { signIn, setAuthHost } = useAuth()
  const { setMode } = useAvatarMode()

  const emailError = isSubmitted ? getEmailValidationError(authEmail) : ""

  const onLogin = async () => {
    setIsSubmitted(true)

    if (!authEmail || !authPassword || emailError) {
      return
    }

    try {
      setIsLoading(true)
      logSentryBreadcrumb("login_attempt")
      const response = await signIn({ email: authEmail, password: authPassword })

      // User is forced to change their password here:
      if (response.kind === "error" && response.message === "NEW_PASSWORD_REQUIRED") {
        console.log("New password required")
        router.replace({
          pathname: "/unauth/update-password",
          params: {
            email: authEmail,
            // Encode the password to handle special characters (e.g. APP-514)
            password: encodeURIComponent(authPassword),
          },
        })
        return
      }

      if (response.kind !== "ok") {
        const rawMessage = response.message || ""

        // Tokens are set but backend is incompatible; navigate into the app so the
        // layout's version gate can redirect to the outdated screen.
        if (rawMessage === "VERSION_MISMATCH") {
          router.replace("/")
          return
        }

        const errorMessage = rawMessage.toLowerCase().includes("sponsor_id not found")
          ? "This account does not currently have access to any channels on the Sidekick Platform."
          : rawMessage || "An unknown error occurred. Please try again."
        Alert.alert("Error", errorMessage)
      } else {
        const isInternal = isInternalEmail(authEmail)
        // Can associate email address with user with "$set: { email: authEmail }"
        posthog?.capture("login_success", { $set: { isInternal }, skPlatform: "app" })
        logSentryBreadcrumb("login_success")

        if (response.requiresSponsorSelection) {
          router.replace("/setup/select-sponsor")
        } else {
          // Single-channel: no select-sponsor screen; set avatar large for welcome before navigating
          setMode("large", false)
          router.replace("/")
        }
      }
    } catch (error: any) {
      console.log("Login Error", error)
      logSentryBreadcrumb("login_error")
      Alert.alert("Error", "An unknown error occurred. Please try again.")
    } finally {
      setIsLoading(false)
    }
  }

  const onBackToLibrary = useCallback(() => {
    if (pendingChannelId) {
      router.replace({
        pathname: "/unauth/channel-library/[id]",
        params: { id: pendingChannelId },
      })
    }
  }, [pendingChannelId])

  const onOpenEnvPicker = useCallback(() => {
    Keyboard.dismiss()
    hostPickerSheetRef.current?.present()
  }, [])

  const onForgotPassword = useCallback(() => {
    Keyboard.dismiss()

    router.push({
      pathname: "/unauth/forgot-password",
      params: { email: authEmail },
    })
  }, [authEmail])

  const onSelectNewHost = useCallback(
    (selectedHost: string) => {
      posthog?.capture("switch_auth_host", { host: selectedHost })
      setAuthHost(selectedHost)
      hostPickerSheetRef.current?.dismiss()
      authEmailInput.current?.focus()
    },
    [setAuthHost, posthog],
  )

  const ModalProvider =
    Platform.OS === "web" ? WebBottomSheetModalProvider : BottomSheetModalProvider

  return (
    <ModalProvider>
      <TouchableWithoutFeedback onPress={Keyboard.dismiss} accessible={false}>
        <KeyboardAvoidingView
          style={[
            themed($screenContentContainer),
            // When the keyboard is open on web, cap the container to the
            // visual viewport height so the ScrollView never extends behind
            // the keyboard and the Login button is not cropped.
            //
            // We use maxHeight (not height + flex:0) because `flex: 0` in
            // React Native Web translates to CSS `flex: 0 1 0px`, which sets
            // flex-basis:0 and collapses the element to 0px even when an
            // explicit height is provided. maxHeight works with the existing
            // flex:1 and correctly limits the rendered height.
            //
            // We intentionally skip marginTop / vv.offsetTop: that value
            // changes rapidly during keyboard animation causing stale-state
            // artefacts. The small gap at the very bottom (≤ URL-bar height,
            // ≈ 44px) when vv.offsetTop > 0 is filled by the themed body
            // background and is visually indistinguishable from the form
            // padding.
            Platform.OS === "web" && isWebKeyboardVisible && webViewport.height > 0
              ? { maxHeight: webViewport.bottom }
              : undefined,
          ]}
          // On web the behavior prop is not used — we manage the visual
          // viewport constraint ourselves above. Passing "height" on web
          // causes react-native-keyboard-controller's KeyboardProvider to
          // interfere via absoluteFillObject and override our explicit height.
          behavior={
            Platform.OS === "ios" ? "padding" : Platform.OS === "web" ? undefined : "height"
          }
        >
          <ScrollView
            contentContainerStyle={themed($screenContentInnerResolved)}
            keyboardShouldPersistTaps="handled"
            showsVerticalScrollIndicator={false}
          >
            <View style={themed($titleContainer)}>
              {isChannelDeeplink ? (
                <>
                  <Text
                    testID="login-heading"
                    text="Login"
                    preset="heading"
                    style={themed($heading)}
                  />
                  <View style={$channelBanner}>
                    <BrandBanner
                      imageSource={channelImageSource}
                      title={channelTitle}
                      subtitle={channelSubtitle}
                    />
                  </View>
                </>
              ) : (
                <Image
                  source={require("assets/images/logo-sidekick-platform.png")}
                  style={themed($logo)}
                  contentFit="contain"
                  tintColor={theme.colors.text}
                />
              )}
            </View>

            <View
              style={[
                themed($formContainer),
                isChannelDeeplink && themed($formContainerDeeplinkOffset),
              ]}
            >
              <TextField
                ref={authEmailInput}
                autoFocus
                style={themed($input)}
                inputWrapperStyle={themed($inputWrapper)}
                value={authEmail}
                onChangeText={setAuthEmail}
                autoCapitalize="none"
                autoComplete="email"
                autoCorrect={false}
                keyboardType="email-address"
                placeholderTx="loginScreen.emailInputPlaceholder"
                helper={emailError}
                status={emailError ? "error" : undefined}
                onSubmitEditing={() => authPasswordInput.current?.focus()}
                blurOnSubmit={false}
                returnKeyType="next"
                editable={!isLoading}
              />

              <PasswordTextField
                ref={authPasswordInput}
                style={themed($input)}
                inputWrapperStyle={themed($inputWrapper)}
                value={authPassword}
                onChangeText={setAuthPassword}
                placeholderTx="loginScreen.passwordInputPlaceholder"
                onSubmitEditing={onLogin}
                returnKeyType="go"
                editable={!isLoading}
              />

              <Button
                testID="login-button"
                text={"Login"}
                preset="sidekick"
                onPress={onLogin}
                disabled={isLoading}
                RightAccessory={
                  isLoading
                    ? ({ style }) => (
                        <ActivityIndicator
                          size="small"
                          color={theme.colors.menuButtonText}
                          style={[style, { marginStart: theme.spacing.sm }]}
                        />
                      )
                    : undefined
                }
              />

              {isChannelDeeplink && channelSubscriptionUrl && (
                <Button
                  testID="request-access-button"
                  text={"Request Access"}
                  preset="sidekick"
                  onPress={() => openLinkInBrowser(channelSubscriptionUrl)}
                  disabled={isLoading}
                />
              )}
            </View>

            <TouchableOpacity
              style={themed($forgotPasswordContainer)}
              onPress={onForgotPassword}
              activeOpacity={0.8}
              hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
            >
              <Text testID="forgot-password-text" style={themed($apiUrlText)}>
                {`Forgot Password?`}
              </Text>
            </TouchableOpacity>
          </ScrollView>
        </KeyboardAvoidingView>
      </TouchableWithoutFeedback>

      {/* Outside KeyboardAvoidingView so it stays pinned to the bottom of the screen regardless
          of keyboard state. On Android, KeyboardAvoidingView (behavior="height") shrinks the
          view height when the keyboard opens, causing absolutely-positioned children inside the
          ScrollView to rapidly shift position and appear to render twice. See APP-930.
          On web, hidden while the keyboard is visible to prevent overlapping the login button. */}
      {!isWebKeyboardVisible && (
        <View style={themed($apiUrlContainer)} pointerEvents="none">
          {isChannelDeeplink && (
            <Text style={themed($apiUrlText)}>{"Powered by the Sidekick Platform"}</Text>
          )}
          <Text testID="version-text" style={themed($apiUrlText)}>
            {`v${semverString({
              version: getAppVersion(),
              build: hostFirstSegment !== "prod" ? getNativeBuildNumber() : null,
              sha: hostFirstSegment !== "prod" ? getWebGitSha() : null,
            })}`}
          </Text>
        </View>
      )}

      {/* Close button — only shown when the user arrived from a channel library so they
          can navigate back without losing their place. Pinned outside KeyboardAvoidingView
          so it stays at the screen corner when the keyboard opens. */}
      {showBackToLibrary && (
        <CloseButton
          onPress={onBackToLibrary}
          accessibilityLabel="Back to content library"
          style={[
            { zIndex: 2100, top: insets.top + 8, right: 16 },
            Platform.OS === "web" ? ({ position: "fixed" } as any) : { position: "absolute" },
          ]}
        />
      )}

      {/* Outside KeyboardAvoidingView so it stays pinned to the screen corner regardless of
          keyboard state. When the keyboard is open it is naturally covered by the keyboard,
          preventing any interference with the login button. (See APP-928) */}
      {Platform.OS !== "web" && (
        <TouchableOpacity
          style={themed($invisibleButton)}
          onLongPress={onOpenEnvPicker}
          delayLongPress={1000}
        >
          {!isDefaultHost && <Text style={themed($envText)}>{hostFirstSegment}</Text>}
        </TouchableOpacity>
      )}

      <BottomSheetListModal
        ref={hostPickerSheetRef}
        heading="Select Environment"
        items={Config.authHosts}
        onItemPress={onSelectNewHost}
      />
    </ModalProvider>
  )
}

const $screenContentContainer: ThemedStyle<ViewStyle> = () => ({
  flex: 1,
})

const $screenContentInner: ThemedStyle<ViewStyle> = ({ spacing }) => ({
  flexGrow: 1,
  paddingVertical: spacing.xxl,
  paddingHorizontal: spacing.lg,
})

const $screenContentInnerWeb: ThemedStyle<ViewStyle> = ({ spacing }) => ({
  flexGrow: 1,
  width: "100%",
  maxWidth: 430,
  alignSelf: "center",
  paddingVertical: spacing.xxl,
  paddingHorizontal: spacing.lg,
})

const $screenContentInnerResolved =
  Platform.OS === "web" ? $screenContentInnerWeb : $screenContentInner

const $titleContainerWeb: ThemedStyle<ViewStyle> = ({ spacing }) => ({
  justifyContent: "center",
  alignItems: "center",
  marginTop: spacing.xxl,
})

const $titleContainerNative: ThemedStyle<ViewStyle> = ({ spacing }) => ({
  justifyContent: "center",
  alignItems: "center",
  marginTop: spacing.xxl,
})

const $titleContainer = Platform.OS === "web" ? $titleContainerWeb : $titleContainerNative

const $formContainerWeb: ThemedStyle<ViewStyle> = ({ spacing }) => ({
  width: "100%",
  gap: spacing.md,
  marginTop: spacing.xxl,
  marginBottom: spacing.xl,
})

const $formContainerNative: ThemedStyle<ViewStyle> = ({ spacing }) => ({
  gap: spacing.md,
  marginTop: spacing.xxl,
  marginBottom: spacing.xl,
})

const $formContainer = Platform.OS === "web" ? $formContainerWeb : $formContainerNative

const $formContainerDeeplinkOffset: ThemedStyle<ViewStyle> = ({ spacing }) => ({
  marginTop: spacing.xxxl,
})

const $logo: ThemedStyle<ImageStyle> = () => ({
  width: 270,
  height: 68,
})

const $inputWeb: ThemedStyle<TextStyle> = ({ colors }) => ({
  color: colors.text,
})

const $inputNative: ThemedStyle<TextStyle> = ({ colors }) => ({
  color: colors.inputText,
})

const $input = Platform.OS === "web" ? $inputWeb : $inputNative

const $inputWrapperWeb: ThemedStyle<ViewStyle> = ({ colors }) => ({
  backgroundColor: colors.inputBackground,
  borderColor: colors.inputBorder,
  borderWidth: 2,
})

const $inputWrapperNative: ThemedStyle<ViewStyle> = ({ colors }) => ({
  borderColor: colors.inputBorder,
  borderWidth: 2,
})

const $inputWrapper = Platform.OS === "web" ? $inputWrapperWeb : $inputWrapperNative

const $forgotPasswordContainerWeb: ThemedStyle<ViewStyle> = ({ spacing }) => ({
  alignItems: "center",
  justifyContent: "center",
  paddingVertical: spacing.sm,
  marginTop: spacing.xxs,
})

const $forgotPasswordContainerNative: ThemedStyle<ViewStyle> = ({ spacing }) => ({
  alignItems: "center",
  justifyContent: "center",
  paddingVertical: spacing.sm,
  marginTop: spacing.xxs,
})

const $forgotPasswordContainer =
  Platform.OS === "web" ? $forgotPasswordContainerWeb : $forgotPasswordContainerNative

const $apiUrlContainerWeb: ThemedStyle<ViewStyle> = ({ spacing }) => ({
  alignItems: "center",
  marginTop: spacing.md,
  justifyContent: "center",
  position: "absolute",
  bottom: 20,
  left: 0,
  right: 0,
})

const $apiUrlContainerNative: ThemedStyle<ViewStyle> = ({ spacing }) => ({
  alignItems: "center",
  marginTop: spacing.md,
  justifyContent: "center",
  position: "absolute",
  bottom: 20,
  left: 0,
  right: 0,
})

const $apiUrlContainer = Platform.OS === "web" ? $apiUrlContainerWeb : $apiUrlContainerNative

const $apiUrlTextWeb: ThemedStyle<TextStyle> = ({ colors, typography }) => ({
  color: colors.text,
  fontSize: 12,
  fontFamily: typography.secondary?.normal || typography.primary.normal,
  textAlign: "center",
})

const $apiUrlTextNative: ThemedStyle<TextStyle> = ({ colors, typography }) => ({
  color: colors.text,
  fontSize: 12,
  fontFamily: typography.secondary?.normal || typography.primary.normal,
  textAlign: "center",
})

const $apiUrlText = Platform.OS === "web" ? $apiUrlTextWeb : $apiUrlTextNative

const $invisibleButton: ThemedStyle<ViewStyle> = () => ({
  position: "absolute",
  bottom: 0,
  right: 0,
  width: 100,
  height: 100,
  justifyContent: "flex-end",
  alignItems: "flex-end",
  backgroundColor: "rgba(255, 0, 0, 0)",
})

const $envTextWeb: ThemedStyle<TextStyle> = ({ colors, typography }) => ({
  color: colors.primary,
  fontSize: 12,
  fontFamily: typography.primary.bold,
})

const $envTextNative: ThemedStyle<TextStyle> = ({ colors, typography }) => ({
  color: colors.primary,
  fontSize: 12,
  fontFamily: typography.fonts.figtree.bold,
  marginBottom: 20,
  marginRight: 20,
})

const $envText = Platform.OS === "web" ? $envTextWeb : $envTextNative

const $channelBanner: ViewStyle =
  Platform.OS === "web"
    ? { width: "85%", alignSelf: "center" }
    : { width: "110%", alignSelf: "center" }

const $heading: ThemedStyle<TextStyle> = ({ spacing }) => ({
  marginTop: spacing.md,
  marginBottom: spacing.sm,
})
